import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ButtonColor, ButtonGroup, Icon, TextButton } from '../../../../components';
import { AppRoutes } from '../../../../constants';
import { useGetUserPermissions, useSimActions } from '../../../../hooks';
import { PracticeProspect, ProspectCategory, ProspectStatus } from '../../../../types';
import { isProspectLocked } from '../../../../utils';

interface SimulationDetailsActionsProps {
  prospect: PracticeProspect;
  runCall: () => void;
}

/**
 * Primary action buttons for the simulation details.
 * Handles main actions like Call/Start and Edit.
 */
const SimulationDetailsActions = ({ prospect, runCall }: SimulationDetailsActionsProps) => {
  const navigate = useNavigate();

  const { runReactivate, isReactivating } = useSimActions();

  const { personaId, category, status, isDraft, userId: prospectUserId } = prospect;

  const { getCanUserEditSimInSelfService } = useGetUserPermissions();
  const canUserEditSimInSelfService = getCanUserEditSimInSelfService(category, isDraft, prospectUserId);

  const isCallDisabled = isProspectLocked(prospect);

  const isQuiz = category === ProspectCategory.QUIZ;

  // Navigates to the prospect page.
  const onEdit = useCallback(() => {
    navigate(`${AppRoutes.PROSPECT}/${personaId}`);
  }, [personaId, navigate]);

  const onUnarchive = useCallback(() => {
    runReactivate({ id: personaId });
  }, [personaId, runReactivate]);

  return (
    <>
      {status === ProspectStatus.ACTIVE && (
        <ButtonGroup fullWidth>
          <TextButton
            color={ButtonColor.TERTIARY}
            disabled={isCallDisabled}
            startIcon={isCallDisabled ? Icon.LOCK : Icon.PHONE}
            text={isQuiz ? 'Start' : 'Call'}
            onClick={runCall}
          />
          <TextButton
            color={ButtonColor.NEUTRAL}
            disabled={!canUserEditSimInSelfService}
            startIcon={Icon.EDIT}
            text="Edit"
            onClick={onEdit}
          />
        </ButtonGroup>
      )}
      {/*
       * Archived simulations are only accessible to managers/admins,
       * so no need to check user permissions here.
       */}
      {status === ProspectStatus.ARCHIVED && (
        <div className="flex justify-center">
          <TextButton loading={isReactivating} text="Unarchive" onClick={onUnarchive} />
        </div>
      )}
    </>
  );
};

export default SimulationDetailsActions;
